import { useState, useEffect } from 'react';
import { X, Keyboard, Command } from 'lucide-react';

interface ShortcutCategory {
    name: string;
    shortcuts: {
        keys: string[];
        description: string;
    }[];
}

const shortcutCategories: ShortcutCategory[] = [
    {
        name: 'Playback',
        shortcuts: [
            { keys: ['Space'], description: 'Play / Pause video' },
            { keys: ['←'], description: 'Skip backward 5 seconds' },
            { keys: ['→'], description: 'Skip forward 5 seconds' },
            { keys: ['Home'], description: 'Go to start' },
            { keys: ['End'], description: 'Go to end' },
        ],
    },
    {
        name: 'Editing',
        shortcuts: [
            { keys: ['Ctrl', 'Z'], description: 'Undo' },
            { keys: ['Ctrl', 'Y'], description: 'Redo' },
            { keys: ['Ctrl', 'Shift', 'Z'], description: 'Redo (alternate)' },
            { keys: ['Delete'], description: 'Delete selected overlay' },
            { keys: ['Backspace'], description: 'Delete selected overlay' },
            { keys: ['Ctrl', 'D'], description: 'Duplicate selected overlay' },
            { keys: ['Ctrl', 'A'], description: 'Select all overlays' },
            { keys: ['Ctrl', 'L'], description: 'Lock / Unlock selected' },
            { keys: ['Escape'], description: 'Deselect all' },
        ],
    },
    {
        name: 'Nudge & Layers',
        shortcuts: [
            { keys: ['↑ ↓ ← →'], description: 'Nudge overlay 1 px' },
            { keys: ['Shift', '↑ ↓ ← →'], description: 'Nudge overlay 10 px' },
            { keys: ['Ctrl', ']'], description: 'Bring forward' },
            { keys: ['Ctrl', '['], description: 'Send backward' },
            { keys: ['Ctrl', 'Shift', ']'], description: 'Bring to front' },
            { keys: ['Ctrl', 'Shift', '['], description: 'Send to back' },
        ],
    },
    {
        name: 'Tools',
        shortcuts: [
            { keys: ['V'], description: 'Select tool' },
            { keys: ['R'], description: 'Rectangle tool' },
            { keys: ['U'], description: 'Rounded rectangle tool' },
            { keys: ['C'], description: 'Circle tool' },
            { keys: ['P'], description: 'Polygon tool' },
            { keys: ['T'], description: 'Text tool' },
            { keys: ['I'], description: 'Image/Logo tool' },
            { keys: ['B'], description: 'Blur tool' },
        ],
    },
    {
        name: 'View',
        shortcuts: [
            { keys: ['+'], description: 'Zoom in timeline' },
            { keys: ['-'], description: 'Zoom out timeline' },
            { keys: ['0'], description: 'Reset zoom' },
        ],
    },
    {
        name: 'Project',
        shortcuts: [
            { keys: ['Ctrl', 'S'], description: 'Save project' },
            { keys: ['Ctrl', 'O'], description: 'Open project' },
            { keys: ['Ctrl', 'N'], description: 'New project' },
            { keys: ['Ctrl', 'E'], description: 'Export video' },
        ],
    },
];

interface KeyboardShortcutsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export const KeyboardShortcutsModal = ({ isOpen, onClose }: KeyboardShortcutsModalProps) => {
    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-black/60 backdrop-blur-sm" 
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-3xl max-h-[80vh] m-4 glass rounded-2xl shadow-2xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                            <Keyboard size={20} className="text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-editor-text">Keyboard Shortcuts</h2>
                            <p className="text-xs text-editor-text-muted">Speed up your workflow with these shortcuts</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-editor-text-muted" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {shortcutCategories.map((category) => (
                            <div key={category.name} className="space-y-3">
                                <h3 className="text-sm font-semibold text-editor-accent uppercase tracking-wider">
                                    {category.name}
                                </h3>
                                <div className="space-y-2">
                                    {category.shortcuts.map((shortcut, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between p-2 rounded-lg bg-editor-surface/50 hover:bg-editor-surface transition-colors"
                                        >
                                            <span className="text-sm text-editor-text-muted">
                                                {shortcut.description}
                                            </span>
                                            <div className="flex items-center gap-1">
                                                {shortcut.keys.map((key, keyIndex) => (
                                                    <span key={keyIndex} className="flex items-center">
                                                        <kbd className="px-2 py-1 text-xs font-mono font-semibold text-editor-text bg-editor-hover border border-editor-border rounded-md shadow-sm min-w-[28px] text-center">
                                                            {key === 'Ctrl' ? (
                                                                <span className="flex items-center gap-0.5">
                                                                    <Command size={10} />
                                                                    <span>Ctrl</span>
                                                                </span>
                                                            ) : key}
                                                        </kbd>
                                                        {keyIndex < shortcut.keys.length - 1 && (
                                                            <span className="text-editor-text-dim mx-0.5">+</span>
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-white/10 bg-editor-surface/30">
                    <div className="flex items-center justify-between">
                        <p className="text-xs text-editor-text-muted">
                            Press <kbd className="px-1.5 py-0.5 text-xs font-mono bg-editor-hover border border-editor-border rounded">?</kbd> anytime to show this help
                        </p>
                        <button
                            onClick={onClose}
                            className="btn btn-primary text-sm"
                        >
                            Got it!
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Hook to manage keyboard shortcuts modal state
export const useKeyboardShortcutsModal = () => {
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Show modal on "?" key (Shift + /)
            if (e.key === '?' && !e.ctrlKey && !e.altKey) {
                e.preventDefault();
                setIsOpen(true);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return {
        isOpen,
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
    };
};

